import type { backendOld } from "~/api";
import { isDefined } from "~/shared/common";
import type { TextSnapshot, TextSnapshotSource } from "~/shared/types";
import { makeDuid } from "~/utils/common";
import { textSnapshotComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const getPaginationKey = (source: TextSnapshotSource) => `textSnapshots:${source.entityName}:${source.entityDuid}`;

export type State = {
  _duidsToTextSnapshots: Map<string, TextSnapshot>;
};

export const state: State = {
  _duidsToTextSnapshots: new Map(),
};

export type Getters = {
  getTextSnapshotByDuidIfLoaded: (duid: string) => TextSnapshot | undefined;
  getLoadedTextSnapshotsForSourceOrdered: (source: TextSnapshotSource) => TextSnapshot[];
};

export type Actions = {
  loadTextSnapshots: (
    ...args: Parameters<typeof backendOld.entityList.textSnapshot>
  ) => ReturnType<typeof backendOld.entityList.textSnapshot>;
  loadPaginatedTextSnapshots: (source: TextSnapshotSource) => Promise<number>;
  updateTextSnapshotName: (
    duid: string,
    name: string,
    options?: { awaitBackend?: boolean }
  ) => Promise<TextSnapshot | undefined>;
  createTextSnapshotFromRestore: (source: TextSnapshot) => Promise<TextSnapshot>;
  $createOrUpdateTextSnapshot: (snapshot: TextSnapshot) => void;
  $deleteTextSnapshot: (snapshot: TextSnapshot) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getTextSnapshotByDuidIfLoaded() {
    return (duid) => this._duidsToTextSnapshots.get(duid);
  },
  getLoadedTextSnapshotsForSourceOrdered() {
    return (source) => {
      const paginationState = this.$getPaginationState(getPaginationKey(source), null);
      if (!paginationState) {
        return [];
      }
      return [...paginationState.duids.values()]
        .map((e) => this.getTextSnapshotByDuidIfLoaded(e))
        .filter(isDefined)
        .sort(textSnapshotComparator);
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadTextSnapshots(...args) {
    const response = await this.$backendOld.entityList.textSnapshot(...args);
    response.data.results.forEach((snapshot) => this.$createOrUpdateTextSnapshot(snapshot));
    return response;
  },
  async loadPaginatedTextSnapshots(source) {
    const key = getPaginationKey(source);
    const paginationState = this.$getPaginationState(key, null);
    const { data } = await this.loadTextSnapshots(
      { entityName: source.entityName, entityDuid: source.entityDuid },
      paginationState?.duids.size ?? 0
    );
    if (!paginationState) {
      this.$createPaginationState(key, null, (a, b) => textSnapshotComparator(b, a));
    }
    this.$addEntitiesToPaginationState(key, null, data.results, data.count);
    return data.count;
  },
  async updateTextSnapshotName(duid, name, options = {}) {
    const { awaitBackend = false } = options;
    const snapshot = this.getTextSnapshotByDuidIfLoaded(duid);
    if (!snapshot) {
      return undefined;
    }
    snapshot.name = name;
    const backendAction = this.$backend.textSnapshot.update({ duid, name });
    if (awaitBackend) {
      await backendAction;
    }
    return snapshot;
  },
  async createTextSnapshotFromRestore(source) {
    const snapshot: TextSnapshot = {
      duid: makeDuid(),
      entityName: source.entityName,
      entityDuid: source.entityDuid,
      text: source.text,
      name: "",
      sourceSnapshotDuid: source.duid,
      actorDuid: this.$useUserStore().duid,
      createdAt: new Date().toISOString(),
    };
    await this.$backend.textSnapshot.create({ duid: snapshot.duid, sourceDuid: source.duid });
    this.$createOrUpdateTextSnapshot(snapshot);
    return snapshot;
  },
  $createOrUpdateTextSnapshot(snapshot) {
    const current = this.getTextSnapshotByDuidIfLoaded(snapshot.duid);
    if (!current) {
      this._duidsToTextSnapshots.set(snapshot.duid, snapshot);
    } else {
      Object.assign(current, snapshot);
    }
    const source: TextSnapshotSource = {
      entityName: snapshot.entityName,
      entityDuid: snapshot.entityDuid,
    };
    this.$addEntitiesToPaginationState(getPaginationKey(source), null, [snapshot], null);
  },
  $deleteTextSnapshot(snapshot) {
    this._duidsToTextSnapshots.delete(snapshot.duid);
    this.$removeEntitiesFromPaginationStates([snapshot]);
  },
};

export { actions, getters };
